import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Medal, Clock, CheckCircle2, Loader2, Crown, Sparkles } from "lucide-react";
import { api } from "@/context/AuthContext";

const ROBOTS = ["/robots/robot_a.png", "/robots/robot_b.png", "/robots/hero_robot.png"];
const robotFor = (id = "") => ROBOTS[(id.charCodeAt(0) + (id.charCodeAt(id.length - 1) || 0)) % ROBOTS.length];

const PODIUM = {
  1: { color: "#FFD700", glow: "rgba(255,215,0,0.45)", icon: Trophy },
  2: { color: "#C0C0C0", glow: "rgba(192,192,192,0.35)", icon: Medal },
  3: { color: "#CD7F32", glow: "rgba(205,127,50,0.35)", icon: Medal },
};

const useCountdown = (endsAt) => {
  const [left, setLeft] = useState({ days: 0, hours: 0 });
  useEffect(() => {
    if (!endsAt) return;
    const tick = () => {
      const ms = Math.max(0, new Date(endsAt).getTime() - Date.now());
      setLeft({ days: Math.floor(ms / 86400000), hours: Math.floor((ms % 86400000) / 3600000) });
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [endsAt]);
  return left;
};

const monthLabel = (mk, lang) => {
  try {
    const d = new Date(`${mk}-15T00:00:00Z`);
    return d.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { month: "long", year: "numeric", timeZone: "UTC" });
  } catch {
    return mk;
  }
};

export const AwardsArena = ({ t, lang }) => {
  const a = t.awards;
  const [tab, setTab] = useState("active");
  const [current, setCurrent] = useState(null);
  const [hall, setHall] = useState(null);
  const countdown = useCountdown(current?.ends_at);

  useEffect(() => {
    api.get("/awards/current").then(({ data }) => setCurrent(data)).catch(() => {});
    api.get("/awards/hall-of-fame").then(({ data }) => setHall(data)).catch(() => setHall([]));
  }, []);

  const prizeLabels = { 1: a.grandPrize, 2: a.mediumPrize, 3: a.basicPrize };
  const placeLabels = { 1: a.place1, 2: a.place2, 3: a.place3 };

  return (
    <div className="max-w-3xl" data-testid="awards-arena">
      <p className="text-sm text-[#A0A0B0] -mt-4 mb-6">{a.sub}</p>

      {/* Tab toggle */}
      <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1 mb-8 w-fit" data-testid="awards-tab-toggle">
        {[
          { id: "active", label: a.tabActive, icon: Trophy },
          { id: "hall", label: a.tabHall, icon: Crown },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`awards-tab-${id}`}
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-4 py-2.5 rounded-full transition-colors ${
              tab === id ? "bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/40 shadow-[0_0_18px_rgba(255,215,0,0.2)]" : "text-white/50 hover:text-white border border-transparent"
            }`}
          >
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {tab === "active" && (
          <motion.div key="active" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} data-testid="active-prizes-panel">
            {/* Countdown */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-[#0F0F1A]/70 border border-[#00F5FF]/25 rounded-2xl px-6 py-5 mb-8" data-testid="awards-countdown">
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center bg-[#00F5FF]/10 border border-[#00F5FF]/30 text-[#00F5FF] shrink-0">
                <Clock size={20} />
              </span>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-1">{a.countdown}</p>
                <p className="font-heading font-black text-xl text-white">
                  <span className="text-[#00F5FF]">{countdown.days}</span> {a.days}, <span className="text-[#00F5FF]">{countdown.hours}</span> {a.hours}
                </p>
              </div>
            </div>

            {/* 3 podium prize cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(current?.prizes || []).map((p) => {
                const cfg = PODIUM[p.place];
                const Icon = cfg.icon;
                return (
                  <motion.div
                    key={p.place}
                    whileHover={{ y: -4 }}
                    data-testid={`prize-card-${p.place}`}
                    className={`relative rounded-3xl border p-6 overflow-hidden bg-[#0F0F1A]/80 ${p.place === 1 ? "md:-mt-3 md:mb-3" : ""}`}
                    style={{ borderColor: `${cfg.color}50`, boxShadow: `0 0 ${p.place === 1 ? 35 : 18}px ${cfg.glow}` }}
                  >
                    <div className="absolute -top-14 -right-14 w-40 h-40 blur-[80px] opacity-30 rounded-full pointer-events-none" style={{ backgroundColor: cfg.color }} />
                    <span
                      className="w-12 h-12 rounded-2xl flex items-center justify-center border mb-4"
                      style={{ color: cfg.color, borderColor: `${cfg.color}50`, backgroundColor: `${cfg.color}12`, boxShadow: `0 0 20px ${cfg.glow}` }}
                    >
                      <Icon size={22} />
                    </span>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1" style={{ color: cfg.color }}>
                      {placeLabels[p.place]} · {prizeLabels[p.place]}
                    </p>
                    <p className="font-heading font-black text-sm text-white leading-snug">
                      {lang === "es" ? p.title_es : p.title_en}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {tab === "hall" && (
          <motion.div key="hall" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} data-testid="hall-of-fame-panel">
            {hall === null ? (
              <div className="flex justify-center py-16 text-white/40"><Loader2 size={24} className="animate-spin" /></div>
            ) : hall.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-white/10 rounded-3xl" data-testid="hall-empty">
                <Sparkles size={32} className="mx-auto text-white/20 mb-3" />
                <p className="text-sm text-white/40">{a.emptyHall}</p>
              </div>
            ) : (
              <div className="space-y-8">
                {hall.map((m) => (
                  <div key={m.month} data-testid={`hall-month-${m.month}`}>
                    <h3 className="font-heading font-black text-sm uppercase tracking-widest text-white/70 mb-4 flex items-center gap-2">
                      <Crown size={15} className="text-[#FFD700]" /> {a.winnersOf} — {monthLabel(m.month, lang)}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {(m.winners || []).map((wn) => {
                        const cfg = PODIUM[wn.place] || PODIUM[3];
                        return (
                          <div
                            key={wn.place}
                            data-testid={`winner-card-${m.month}-${wn.place}`}
                            className="relative rounded-2xl border bg-[#0F0F1A]/80 p-5 overflow-hidden"
                            style={{ borderColor: `${cfg.color}40`, boxShadow: `0 0 14px ${cfg.glow}` }}
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <img
                                src={robotFor(wn.avatar_name)}
                                alt={wn.avatar_name}
                                className="w-10 h-10 rounded-full object-cover border"
                                style={{ borderColor: cfg.color, boxShadow: `0 0 14px ${cfg.glow}` }}
                              />
                              <div className="min-w-0">
                                <p className="font-heading font-black text-sm text-white truncate">{wn.avatar_name}</p>
                                <p className="text-[10px] text-white/40">{a.owner}: {wn.owner_masked}</p>
                              </div>
                              <span className="ml-auto font-heading font-black text-lg" style={{ color: cfg.color }}>#{wn.place}</span>
                            </div>
                            <p className="text-[11px] text-white/60 mb-1">❤️ {wn.likes.toLocaleString()} {a.likesWon}</p>
                            <p className="text-[10px] text-white/40 mb-3">
                              {a.winningConfig}: <span className="font-mono text-[#00F5FF]">[{(wn.matrix || []).join(", ")}]</span>
                            </p>
                            <span className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-[#00FFAA] bg-[#00FFAA]/5 border border-[#00FFAA]/30 px-2.5 py-1 rounded-full" data-testid="disbursed-badge">
                              <CheckCircle2 size={10} /> {a.disbursed}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
